import React from 'react';
import Select from 'react-select';

import Modal from '../shared/components/core-ui/modal/modal.component';
import { HousekeepingAssigneeOption, HousekeepingOrderItem } from './types';

interface SelectOption {
  label: string;
  value: string;
}

interface HouseKeepingAssignModalProps {
  open: boolean;
  mode: 'single' | 'bulk';
  order: HousekeepingOrderItem | null;
  projectOptions?: SelectOption[];
  unitOptions?: SelectOption[];
  selectedProject?: string;
  selectedUnits?: string[];
  options: HousekeepingAssigneeOption[];
  selectedUserIds: string[];
  saving?: boolean;
  onProjectChange?: (projectId: string) => void;
  onUnitsChange?: (units: string[]) => void;
  onChange: (userIds: string[]) => void;
  onClose: () => void;
  onSubmit: () => void | Promise<void>;
}

const HouseKeepingAssignModal: React.FC<HouseKeepingAssignModalProps> = ({
  open,
  mode,
  order,
  projectOptions = [],
  unitOptions = [],
  selectedProject = '',
  selectedUnits = [],
  options,
  selectedUserIds,
  saving,
  onProjectChange,
  onUnitsChange,
  onChange,
  onClose,
  onSubmit
}) => {
  const selectedOption = options.find((option) => selectedUserIds.includes(option.value)) || null;
  const selectedUnitOptions = unitOptions.filter((option) => selectedUnits.includes(option.value));
  const selectedProjectOption =
    projectOptions.find((option) => option.value === selectedProject) || null;
  const isBulk = mode === 'bulk';
  const modalTitle = isBulk ? 'تعيين فريق النظافة للمشروع أو الوحدات' : 'تعيين فريق النظافة للطلب';

  const handleUsersChange = (value: any) => {
    onChange(value ? [value.id || value.value] : []);
  };

  const handleProjectSelect = (value: any) => {
    onProjectChange?.(value?.value || '');
  };

  const handleUnitsSelect = (values: any) => {
    onUnitsChange?.((values || []).map((value: any) => value.value));
  };

  return (
    <Modal
      config={{
        showModal: open,
        fullScreen: false,
        showClose: true,
        maxWidth: 'sm'
      }}
      eventHandlers={{
        closeModalHandler: onClose
      }}
    >
      <div className="p-6" dir="rtl" lang="ar">
        <h2 className="text-2xl text-center font-bold text-slate-900">{modalTitle}</h2>

        {isBulk ? (
          <div className="mt-5 space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-base font-gilroy-medium  font-medium text-neutral-500">المشروع</p>
              <div className="mt-2">
                <Select
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                  options={projectOptions}
                  value={selectedProjectOption}
                  isClearable
                  placeholder="اختر المشروع"
                  onChange={handleProjectSelect}
                />
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-base font-gilroy-medium  font-medium text-neutral-500">الوحدات</p>
              <div className="mt-2">
                <Select
                  isMulti
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                  options={unitOptions}
                  value={selectedUnitOptions}
                  placeholder="اختر وحدة أو أكثر"
                  onChange={handleUnitsSelect}
                />
              </div>
              <p className="mt-2 text-base font-gilroy-medium  font-medium text-neutral-500">
                لو اخترت مشروع فقط بدون وحدات، تعيين ينطبق على كل طلبات مشروع ظاهرة حالياً.
              </p>
            </div>
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-base font-gilroy-medium  font-medium text-neutral-500">المشروع</p>
              <p className="mt-1 text-sm font-bold text-slate-900">{order?.projectName || '-'}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-base font-gilroy-medium  font-medium text-neutral-500">الوحدة</p>
              <p className="mt-1 text-sm font-bold text-slate-900">{order?.unitName || '-'}</p>
            </div>
          </div>
        )}

        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-sm font-bold text-slate-800">
            {isBulk ? 'موظف النظافة' : 'موظفو النظافة'}
          </p>
          <div className="mt-3">
            <Select
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              options={options}
              value={selectedOption}
              placeholder={isBulk ? 'اختر موظف النظافة' : 'اختر موظف النظافة'}
              onChange={handleUsersChange}
              isClearable
            />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 px-4 py-2 text-lg  font-semibold text-slate-700"
          >
            إلغاء
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={onSubmit}
            className="rounded-xl bg-emerald-600 px-4 py-2 text-lg font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? 'جاري الحفظ...' : 'حفظ التعيين'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default HouseKeepingAssignModal;
